/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.applySchedule();
      this.bindEvents();
      this.init();
      this.initRegion();
    }

    disconnectedCallback() {
      clearTimeout(this.slideTimer);
      if (this.regionRefresh) document.removeEventListener(this.regionEvent, this.regionRefresh);
      if (this.regionPageShow) window.removeEventListener('pageshow', this.regionPageShow);
      if (this.observer) this.observer.disconnect();
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
      if (this.resizeHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeHandler);
      }

      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        const area = this.slider.parentElement;
        area.addEventListener('mouseenter', this.pauseSlider.bind(this));
        area.addEventListener('mouseleave', this.resumeSlider.bind(this));
        area.addEventListener('focusin', this.pauseSlider.bind(this));
        area.addEventListener('focusout', (evt) => {
          if (!area.contains(evt.relatedTarget)) this.resumeSlider();
        });

        area.querySelectorAll('.announcement__arrow').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.stopAutoplay();
            this.goTo(this.index + (btn.name === 'next' ? 1 : -1));
          });
        });

        // A swipe (or horizontal trackpad scroll) means the shopper has taken control.
        this.slider.addEventListener('touchstart', () => {
          this.scrollTarget = null;
          this.stopAutoplay();
        }, { passive: true });
        this.slider.addEventListener('wheel', (evt) => {
          if (Math.abs(evt.deltaX) > Math.abs(evt.deltaY)) {
            this.scrollTarget = null;
            this.stopAutoplay();
          }
        }, { passive: true });
        this.slider.addEventListener('scroll', debounce(this.syncToScroll.bind(this), 100));

        this.visibilityHandler = () => this.scheduleNext();
        document.addEventListener('visibilitychange', this.visibilityHandler);

        this.resizeHandler = () => this.goTo(this.index, false);
        window.addEventListener('on:debounced-resize', this.resizeHandler);

        // Theme editor: show the selected block and hold it there.
        this.addEventListener('shopify:block:select', (evt) => {
          const index = this.slides.indexOf(evt.target);
          if (index < 0) return;
          this.pauseSlider();
          this.goTo(index, false);
        });
        this.addEventListener('shopify:block:deselect', this.resumeSlider.bind(this));
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        this.slides = Array.from(this.slider.querySelectorAll('.announcement__text'));
        this.slides.forEach((slide, i) => {
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          slide.setAttribute('aria-label', `${i + 1} ${this.dataset.ofLabel} ${this.slides.length}`);
        });
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion;
        this.inView = true;
        this.setActive(0);

        if (this.autoplay) {
          this.observer = new IntersectionObserver((entries) => {
            this.inView = entries[0].isIntersecting;
            this.scheduleNext();
          });
          this.observer.observe(this);
        }
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Drops scheduled slides that aren't live right now. The page may be served from cache, so
     * the start/end times rendered by Liquid are checked again against the visitor's clock.
     */
    applySchedule() {
      const track = this.querySelector('.announcement__track');
      if (!track) return;

      const now = Date.now() / 1000;
      track.querySelectorAll('.announcement__text').forEach((slide) => {
        const { start, end } = slide.dataset;
        if ((start && now < Number(start)) || (end && now >= Number(end))) {
          slide.remove();
        } else {
          slide.hidden = false;
        }
      });

      const slides = track.querySelectorAll('.announcement__text');
      if (!slides.length) {
        this.classList.add('announcement--empty');
        this.applyColors();
      } else {
        this.applyColors(slides[0]);
      }

      if (this.slider && slides.length < 2) {
        this.querySelectorAll('.announcement__arrow').forEach((btn) => btn.remove());
        this.slider.classList.remove('announcement__slider');
        this.slider.removeAttribute('aria-roledescription');
        this.slider.removeAttribute('aria-live');
        this.slider = null;
      }
    }

    /**
     * UAE-only slides (data-region="uae", announcement_slide field `uae_only`, 2026-11-03): hidden
     * once window.tbGeo.resolveOutsideUae() — the theme's single geo source, in <head> — confirms
     * the shopper is outside the UAE. Unknown → shown. Hidden with `hidden`, never removed, and
     * re-checked on an area change and on a BFCache restore, so it can come back.
     */
    initRegion() {
      const track = this.querySelector('.announcement__track');
      if (!track) return;
      this.regionSlides = Array.from(track.querySelectorAll('.announcement__text[data-region="uae"]'));
      if (!this.regionSlides.length) return;
      this.allSlides = Array.from(track.querySelectorAll('.announcement__text'));
      this.regionEvent = (window.tbGeo && window.tbGeo.EMIRATE_CHANGE_EVENT) || 'on:emirate:change';
      this.regionRefresh = () => this.applyRegion();
      this.regionPageShow = (evt) => {
        if (evt.persisted) this.applyRegion();
      };
      document.addEventListener(this.regionEvent, this.regionRefresh);
      window.addEventListener('pageshow', this.regionPageShow);
      if (window.tbGeo || document.readyState !== 'loading') this.applyRegion();
      else document.addEventListener('DOMContentLoaded', this.regionRefresh, { once: true });
    }

    async applyRegion() {
      let abroad = false;
      try {
        abroad = !!(window.tbGeo && window.tbGeo.resolveOutsideUae && await window.tbGeo.resolveOutsideUae());
      } catch (e) {
        abroad = false;
      }
      const first = this.abroad === undefined;
      if (abroad === this.abroad || (first && !abroad)) {
        this.abroad = abroad;
        return;
      }
      this.abroad = abroad;
      this.regionSlides.forEach((slide) => {
        slide.hidden = abroad;
      });

      const visible = this.allSlides.filter((slide) => slide.isConnected && !slide.hidden);
      this.classList.toggle('announcement--empty', !visible.length);
      if (!this.slider) {
        // Static bar (fewer than 2 slides at load): only colors and the empty state change.
        this.applyColors(visible[0]);
        return;
      }

      this.slides = visible;
      const multi = visible.length > 1;
      this.querySelectorAll('.announcement__arrow').forEach((btn) => {
        btn.hidden = !multi;
      });
      visible.forEach((slide, i) => {
        slide.setAttribute('aria-label', `${i + 1} ${this.dataset.ofLabel} ${visible.length}`);
      });
      this.autoplay = multi && this.dataset.autoplay === 'true' && !this.reducedMotion && !this.userStopped;
      if (!visible.length) {
        clearTimeout(this.slideTimer);
        this.applyColors();
        return;
      }
      this.goTo(0, false);
    }

    /**
     * Applies a slide's colors to the whole bar (the section defaults without a slide).
     * @param {Element} [slide] - Slide element with data-bg / data-text.
     */
    applyColors(slide) {
      const { bg, text } = slide ? slide.dataset : {};
      this.style.setProperty('--announcement-bg', bg || this.dataset.defaultBg);
      this.style.setProperty('--announcement-text-color', text || this.dataset.defaultText);
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.nav && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.nav && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.nav && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.nav && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Scrolls to a slide, wrapping around at either end.
     * @param {number} index - Slide index (may be out of range).
     * @param {boolean} [animate=true] - Smooth scroll (ignored for reduced motion).
     */
    goTo(index, animate = true) {
      const count = this.slides.length;
      const target = ((index % count) + count) % count;
      const direction = document.dir === 'rtl' ? -1 : 1;

      // Ignore intermediate scroll positions until this scroll lands.
      this.scrollTarget = target;
      this.slider.scrollTo({
        left: direction * target * this.slider.clientWidth,
        behavior: animate && !this.reducedMotion ? 'smooth' : 'auto'
      });
      this.setActive(target);
    }

    /**
     * Marks a slide as the visible one and applies its colors to the bar.
     * @param {number} index - Slide index.
     */
    setActive(index) {
      this.index = index;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-visible', i === index);
        slide.inert = i !== index;
      });

      this.applyColors(this.slides[index]);
      this.scheduleNext();
    }

    /**
     * Updates the active slide after a swipe or other native scroll.
     */
    syncToScroll() {
      const index = Math.round(Math.abs(this.slider.scrollLeft) / this.slider.clientWidth);
      if (this.scrollTarget != null) {
        if (index === this.scrollTarget) this.scrollTarget = null;
        return;
      }
      if (index !== this.index && this.slides[index]) this.setActive(index);
    }

    /**
     * (Re)starts the countdown to the next slide when autoplay is allowed right now.
     */
    scheduleNext() {
      clearTimeout(this.slideTimer);
      if (!this.autoplay || this.isPaused || !this.inView || document.hidden) return;

      this.slideTimer = setTimeout(() => this.goTo(this.index + 1), this.dataset.slideDelay);
    }

    /**
     * Stops autoplay for the rest of the page view (the shopper has taken control).
     */
    stopAutoplay() {
      this.autoplay = false;
      this.userStopped = true;
      clearTimeout(this.slideTimer);
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
      clearTimeout(this.slideTimer);
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
      this.scheduleNext();
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
